<template>
  <div v-if="!isLoading" class="resource-tagging view">
    <template v-if="singleEmptyIndicator && !isTagged">
      <span>-</span>
    </template>
    <template v-else>
      <dl v-if="!isSubjectLevelTaggingEmpty" class="field-set taggings type-text">
        <dt class="field-label">
          {{ t("resource_tagging_subject_and_level_label") }}
        </dt>
        <dd class="field-value">
          <data-table-list class="subject-and-level-datatable">
            <v-data-table
              :items="sortSubjectLevelTaggings"
              :headers="subjectAndLevelDataTableHeaders"
              :items-per-page-options="subjectAndLevelDataTableFooterProps.itemsPerPageOptions"
              fixed-header
              :mobile-breakpoint="0"
            >
              <template #[`item.subjectName`]="{ item }">
                <data-table-cell class="subject">
                  <span
                    v-if="item.subject && item.subject.subjectName"
                    :title="item.subject.subjectName"
                    class="main"
                  >
                    {{ item.subject.subjectName }}
                  </span>
                  <span v-else class="main"></span>
                </data-table-cell>
              </template>

              <template #[`item.levelAndStream`]="{ item }">
                <data-table-cell class="level">
                  <span
                    v-if="item.levelAndStream && item.levelAndStream.displayName"
                    :title="item.levelAndStream.displayName"
                    class="main"
                  >
                    {{ item.levelAndStream.displayName }}
                  </span>
                  <span v-else class="main"></span>
                </data-table-cell>
              </template>
            </v-data-table>
          </data-table-list>
        </dd>
      </dl>
      <dl v-else-if="!hideEmptyIndicator" class="field-set taggings type-text">
        <dt class="field-label">
          {{ t("resource_tagging_subject_and_level_label") }}
        </dt>
        <dd class="field-value">-</dd>
      </dl>

      <dl v-if="showContentMapTagging" class="field-set taggings type-text">
        <dt class="field-label">{{ contentMapAndTopicTitle }}</dt>
        <dd class="field-value">
          <AccordionWrapper v-if="isTagged && !isLoading" ref="acc1" class="tagging">
            <resource-tagging-item-view
              v-for="(resourceTagging, index) in contentMapTagging"
              :key="resourceTagging.tagId"
              :open="acc1State[index]"
              :resource-tagging="resourceTagging"
              :resource-type="resourceType"
              :subject-level-and-stream-content-map-data="subjectLevelAndStreamContentMapData"
              :hide-empty-indicator="hideEmptyIndicator"
              @cv:change="acc1Change($event, index, resourceTagging.tagId)"
            />
          </AccordionWrapper>
        </dd>
      </dl>
      <dl v-else-if="!hideEmptyIndicator" class="field-set taggings type-text">
        <dt class="field-label">{{ contentMapAndTopicTitle }}</dt>
        <dd class="field-value">-</dd>
      </dl>
    </template>
  </div>
</template>

<script setup lang="ts">
import ResourceTaggingItemView from "@modules/editor/components/resource-taggings/ResourceTaggingItemView.vue";
import resourceTaggingsStore from "@modules/editor/components/resource-taggings/ResourceTaggingsStore";
import { isEmpty } from "@/utils/var.check";
import contentMapApi from "@/apis/content.map.api";
import sortUtils from "@utils/sort.utils.js";
import { ref, computed, watch, onUnmounted, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@/plugins/common/composables/error";
import { useEventBusWatch } from "@plugins/common/composables/event-bus-watch.ts";
import resourceTaggingEventBus from "@modules/editor/components/resource-taggings/service/event-bus.ts";
import { NAMESPACE } from "@modules/editor/service/editor.constant.ts";
import { useStore } from "vuex";

interface Props {
  resourceUuid: string;
  resourceType: string;
  resourceStatus?: string;
  hideEmptyIndicator?: boolean;
  singleEmptyIndicator?: boolean;
  isAdaptiveSection?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  resourceStatus: "APPROVED",
  hideEmptyIndicator: false,
  singleEmptyIndicator: false,
  isAdaptiveSection: false,
});

const emit = defineEmits<{
  loaded: [];
}>();

//Composable
const { trackWatch } = useEventBusWatch();
const store = useStore();
const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

// Data
const isLoading = ref(true);
const isTagged = ref(false);
const acc1ItemCount = ref(0);
const acc1OpenedId = ref<string | null>(null);
const acc1State = ref<boolean[]>([]);
const subjectLevelAndStreamContentMapData = ref<unknown[]>([]);
const subjectAndLevelDataTableHeaders = ref<unknown[]>([]);
const subjectAndLevelDataTableFooterProps = ref<unknown>();

// Computed

const currentPageId = computed(() => store.getters[`${NAMESPACE.LESSON}/currentPageId`]);

const contentMapTagging = computed(() => {
  return resourceTaggingsStore.getContentMapTaggingsByResource(props.resourceUuid);
});

const showContentMapTagging = computed(() => {
  return !isEmpty(contentMapTagging.value);
});

const contentMapAndTopicTitle = computed(() => {
  if (props.isAdaptiveSection) {
    return t("adaptive_learning_content_map_and_learning_outcome_label");
  } else {
    return t("resource_tagging_cm_topic_label");
  }
});

const isSubjectLevelTaggingEmpty = computed(() => {
  return isEmpty(subjectLevelTaggings.value);
});

const subjectLevelTaggings = computed(() => {
  return resourceTaggingsStore.getSubjectAndLevelTaggingsByResource(props.resourceUuid);
});

const sortSubjectLevelTaggings = computed(() => {
  // Check if subjectLevelTaggings is empty
  if (isEmpty(subjectLevelTaggings.value)) return [];

  // Define the sorting configuration
  const sortConfig = [
    { field: "subject.subjectName", asc: true },
    { field: "levelAndStream.displayName", asc: true },
  ];

  // Sort the subjectLevelTaggings using the sortObjects method
  const sortedSubjectLevelTaggings = sortUtils.sortObjects(subjectLevelTaggings.value, sortConfig);

  return sortedSubjectLevelTaggings;
});

// Methods
const initSubjectAndLevelDataTableOption = function () {
  subjectAndLevelDataTableHeaders.value = [
    {
      title: t("search_resource_detail_subject_header_label"),
      class: "subject",
      key: "subjectName",
      sortable: false,
    },
    {
      title: t("search_resource_detail_level_header_label"),
      class: "level",
      key: "levelAndStream",
      sortable: false,
    },
  ];

  subjectAndLevelDataTableFooterProps.value = {
    itemsPerPageOptions: [],
  };
};

const acc1Change = function (ev, changedIndex, changedId) {
  acc1State.value = acc1State.value.map((_item, index) => {
    return index === changedIndex && ev.dataOpen;
  });
  if (ev.dataOpen) {
    acc1OpenedId.value = changedId;
  }
};

const refetchResourceTaggings = async () => {
  resourceTaggingsStore.clearResourceTaggingsByResource(props.resourceUuid);
  await fetchResourceTaggings();
};

const fetchResourceTaggings = async () => {
  isLoading.value = true;
  const fetchResourceTaggingsPromise = resourceTaggingsStore.fetchResourceTaggings({
    uuid: props.resourceUuid,
    resourceType: props.resourceType.toUpperCase(),
    resourceStatus: props.resourceStatus ? props.resourceStatus : "APPROVED",
    includeExpired: true,
  });
  fetchResourceTaggingsPromise.then(
    () => {
      const contentMapIds = [];
      contentMapTagging.value.forEach((resourceTagging) => {
        if (
          !isEmpty(resourceTagging.contentMapMetadata) &&
          !isEmpty(resourceTagging.contentMapMetadata.id)
        ) {
          contentMapIds.push(resourceTagging.contentMapMetadata.id);
        }
      });
      const subjectIds = [];
      subjectLevelTaggings.value.forEach((tagging) => {
        if (!isEmpty(tagging.subject) && !isEmpty(tagging.subject.id)) {
          subjectIds.push(tagging.subject.id);
        }
      });
      if (!isEmpty(contentMapIds) && !isEmpty(subjectIds)) {
        contentMapApi
          .fetchLevelAndStreamContentMapByContentMapIds(
            subjectIds,
            contentMapIds,
            props.hideEmptyIndicator,
          )
          .then(
            (response) => {
              if (response.code !== "ok") {
                passiveApiFailHandler();
                return;
              }

              subjectLevelAndStreamContentMapData.value = response.data;
              isLoading.value = false;
              emit("loaded");
            },
            () => {
              passiveApiFailHandler();
            },
          );
      } else {
        isLoading.value = false;
        emit("loaded");
      }
    },
    () => {
      passiveApiFailHandler();
    },
  ); //OK
};

// Watch
watch(
  contentMapTagging,
  function () {
    if (!isEmpty(contentMapTagging.value)) {
      const newSize = contentMapTagging.value.length;
      if (acc1ItemCount.value !== newSize) {
        acc1State.value = contentMapTagging.value.map((item) => acc1OpenedId.value === item.tagId);
      }
      isTagged.value = true;
    } else {
      isTagged.value = false;
    }
  },
  { deep: true },
);

watch(
  () => props.resourceUuid,
  async () => {
    if (isEmpty(props.resourceUuid)) {
      return;
    }
    await fetchResourceTaggings();
  },
);

onUnmounted(() => {
  resourceTaggingsStore.clearResourceTaggingsByResource(props.resourceUuid);
});

onMounted(async () => {
  if (!isEmpty(props.resourceUuid)) {
    await fetchResourceTaggings();
  }
  initSubjectAndLevelDataTableOption();

  //only listen to emit if i am in module cover
  if (currentPageId.value === "cover") {
    trackWatch(
      resourceTaggingEventBus.$on("refetchResourceTaggingForModuleCover", async () => {
        await refetchResourceTaggings();
      }),
    );
  }

  //only listen to emit if i am in section cover
  if (currentPageId.value === "sectionCover") {
    trackWatch(
      resourceTaggingEventBus.$on("refetchResourceTaggingForSectionCover", async () => {
        await refetchResourceTaggings();
      }),
    );
  }
});
</script>

<style scoped></style>
